// © Microsoft Corporation. All rights reserved.

import { isNil, isString, omitBy } from "lodash-es";

import { TelemetryOrdinalTracker } from "./Telemetry.OrdinalTracker";
import { TelemetryProps } from "./Telemetry.props";
import { TelemetryTags } from "./Telemetry.tags";
import { TelemetryTypes } from ".";
import { Utility } from "@msnews/core";

/**
 * Telemetry class to inject into components via props.
 *
 * @export
 * @class Telemetry
 */
export class Telemetry {

    /**
     * The index to uniquely identify the telemetry elements.
     * @private
     * @type {number}
     */
    public static ordinalTracker: { [id: number]: TelemetryOrdinalTracker } = {
        0: {
            childIndices: [],
            childOrdinalCounter: 0,
            ordinal: 1
        }
    };

    /**
     * The index to uniquely identify the telemetry elements.
     * @private
     * @type {number}
     */
    public static telemetryIndex = 0;

    /**
     * Set the new telemetry props for a child component.
     * @type {function}
     * @param {parentTelemetry} telemetry - props that may contain the telemetry index of the parent component.
     * @param {childTelemetry} telemetry - props that contain the telemetry index of the child component.
     */
    public static setChildProps(parentTelemetry: TelemetryProps, childTelemetry: TelemetryProps): void {
        if (!parentTelemetry) {
            throw "the parentTelemetry parameter is required";
        }

        if (!childTelemetry) {
            throw "the childTelemetry parameter is required";
        }

        // set parentIndex from parent's index; if the parent has no non-zero index, use the grandparent index instead, falling back to zero
        childTelemetry.parentIndex = parentTelemetry.index || parentTelemetry.parentIndex || 0;

         // This part is required to trigger shouldComponentUpdate on child component,
         // if no other props changed but parent component re-renders.
        // childTelemetryProps.headlineIndex = Math.random();
    }

    /**
     * Gets the telemetry item for a child element.
     *
     * @static
     * @param {TelemetryProps} parentTelemetry the telemetry props for the parent element
     * @param {string} telemetryName the telemetry name for the child
     * @param {TelemetryTypes} telemetryType the telemetry type for the child
     * @returns {TelemetryProps} the telemetry props for the child
     * @memberof Telemetry
     */
    public static getChildTelemetryItem(parentTelemetry: TelemetryProps, telemetryName: string, telemetryType: TelemetryTypes): TelemetryProps {
        const initTelemetryProps: TelemetryProps = {
            name: telemetryName,
            telemetryType: telemetryType
        };

        const resultTelemetryProps = Telemetry.createProps(initTelemetryProps, parentTelemetry, initTelemetryProps);
        Telemetry.setChildProps(parentTelemetry, resultTelemetryProps);

        return resultTelemetryProps;
    }

    /**
     * Get the parent's index value for telemetry.
     * @type {function}
     * @param {parentTelemetry} telemetry - props that may contain the telemetry index of the parent component.
     */
    public static getParentIndex(parentTelemetry: TelemetryProps): number {
        if (!parentTelemetry) {
            throw "the parentTelemetry parameter is required";
        }

        // return parentIndex from parent's index; if the parent has no non-zero index, use the grandparent index instead, even if zero
        return parentTelemetry.index || parentTelemetry.parentIndex;
    }

    /**
     * Get the parent's index value for telemetry.
     * @type {function}
     * @param {parentTelemetry} telemetry - props that may contain the telemetry index of the parent component.
     */
    public static getParentHeadlineIndex(parentTelemetry: TelemetryProps): number {
        if (!parentTelemetry) {
            throw "the parentTelemetry parameter is required";
        }

        // This part is required to trigger shouldComponentUpdate on child component,
        // if no other props changed but parent component re-renders.
        return Math.random();
    }

    /**
     * Create the root private telemetry object for an application
     * @type {function}
     * @param {string} name - Name of the entity.
     * @param {string} id - Id property of the entity.
     * @param {number} rootTelemetryIndex - telemetry index of root, e.g. the SPA host container index
     * @returns {TelemetryProps} - private Telemetery props
     */
    public static createRootProps(name?: string, id?: string, rootTelemetryIndex?: number): TelemetryProps {
        const ordinal = 0;

        // root index if specified otherwise we set it the next telemetry index
        const rootIndex = rootTelemetryIndex || Telemetry.getNewIndex();

        Telemetry.ordinalTracker[rootIndex] = {
            ordinal,
            childIndices: {},
            childOrdinalCounter: 0
        };

        return {
            id: id,
            index: rootIndex,
            name: name,
            ordinal: ordinal,
            parentIndex: 0
        };
    }

    /**
     * Create the telemetry object for the component.
     * @type {function}
     * @param {TelemetryProps} savedProps - Props that are being saved by the calling component. If this is the first call to createProps, null or undefined should be passed.
     * @param {TelemetryProps} componentProps - Props that were passed into the calling component. This set of props carries the parentIndex.
     * @param {TelemetryProps} initializationProps - Override props which can be used to replace the default properties
     * @returns {TelemetryProps} - Telemetry props
     */
    public static createProps(savedProps: TelemetryProps, componentProps: TelemetryProps, initializationProps: TelemetryProps = {}): TelemetryProps {

        const parentIndex = componentProps.parentIndex || 0;

        if (!(parentIndex in Telemetry.ordinalTracker)) {
           throw "parentIndex not tracked in ordinalTracker";
        }

        let index: number;
        let ordinal: number;

        // TODO are we calling the right one?
        if (savedProps && !isNil(savedProps.index)) {
            index = savedProps.index;
        } else {
            index = Telemetry.getNewIndex();
        }

        Telemetry.resetChildProps(index);

        const isIndexTrackedByParent: boolean = index in Telemetry.ordinalTracker[parentIndex].childIndices;
        if (isIndexTrackedByParent) {
            ordinal = Telemetry.ordinalTracker[parentIndex].childIndices[index];
        } else {
            ordinal = ++Telemetry.ordinalTracker[parentIndex].childOrdinalCounter;
            Telemetry.ordinalTracker[parentIndex].childIndices[index] = ordinal;
        }

        Telemetry.ordinalTracker[index] = {
            ordinal,
            childIndices: {},
            childOrdinalCounter: 0
        };

        return { ...initializationProps, index, ordinal, parentIndex };
    }

    /**
     * Render telemetry props to the DOM.
     * @type {function}
     * @param {TelemetryProps} props - Props of the element which needs to render telemetry string.
     * @returns {TelemetryTags} - Telemetery tags  for rendering
     */
    public static render(props: TelemetryProps): TelemetryTags {
        if (!props || !props.index) {
            return null;
        }

        const result: any = {
            c: props.category,
            ct: props.contentType,
            f: props.feedId || props.id,
            h: props.headlineIndex,
            i: props.index,
            l: props.id,
            n: props.name,
            o: props.ordinal,
            p: props.parentIndex,
            y: props.telemetryType,
            v: props.vertical
        };

        const filteredResult = omitBy(result, isNil);

        const tags: TelemetryTags = {
            "data-m": JSON.stringify(filteredResult),
            "data-id": filteredResult.i
        };

        if (isString(props.userFacingText)) {
            tags["data-hl"] = props.userFacingText;
        }

        return tags;
    }

    // Bug 15830025: if this isn't used, we should delete it; if it is used, it needs unit tests.
    /**
     * Resets the global dictionary recursively starting at the given component.
     * @type {function}
     * @param {number} telemetryIndex - Telemetry index of the component.
     */
    public static resetChildProps(telemetryIndex: number): void {
        if (!(telemetryIndex in Telemetry.ordinalTracker)) {
            return;
        }

        const childIndices = Telemetry.ordinalTracker[telemetryIndex].childIndices;
        Object.keys(childIndices).map((index) => this.resetChildProps(Utility.toNumber(index)));
        delete Telemetry.ordinalTracker[telemetryIndex];
    }

    /**
     * Decides if the new telemetry related props changed on parent component.
     * @type {function}
     * @param {TelemetryProps} oldProps - Old props of the element which contains parentIndex and ordinal values.
     * @param {TelemetryProps} newProps - New props of the element which contains parentIndex and ordinal values.
     * @param {TelemetryProps} telemetry - Existing private telemetry props of the element.
     * @returns {boolean} - should telemetry props need to be changed.
     */
    public static shouldComponentUpdate(oldProps: TelemetryProps, newProps: TelemetryProps, telemetry: TelemetryProps): boolean {
        return (oldProps.parentIndex !== newProps.parentIndex)
            || (telemetry && !(telemetry.index in Telemetry.ordinalTracker[oldProps.parentIndex].childIndices));
    }

    /**
     * Creates and returns new telemetry index which should be global among the application.
     *
     * @export
     * @return {number}
     */
    private static getNewIndex(): number {
        return --Telemetry.telemetryIndex;
    }
}